import posthog from "posthog-js";

/**
 * Fire-and-forget client analytics. Safe to call anywhere:
 *  - No-op on the server (PostHog only initializes in the browser).
 *  - No-op until consent — `posthog.capture()` respects the opt-out state set by
 *    the consent gate (`PostHogProvider`), so callers never check consent themselves.
 *  - Never throws — analytics is a non-critical side effect and must not break the UI.
 *
 * Internal/authenticated routes (admin, dashboards, Studio) are excluded centrally
 * via PostHog's `before_send` (see `PostHogProvider`), so callers don't filter by path.
 *
 * Call ONLY from client components ("use client"); this module imports posthog-js.
 */
export function track(event: string, properties?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  try {
    posthog.capture(event, properties);
  } catch {
    /* swallow — analytics is non-critical */
  }
}

/**
 * Apply-funnel event names — the drop-off funnel the draft-autosave work targets.
 * Centralised so the names stay stable across the form and any future dashboards.
 */
export const APPLY_EVENTS = {
  started: "apply_started",
  stepCompleted: "apply_step_completed",
  draftResumed: "apply_draft_resumed",
  submitted: "apply_submitted",
} as const;

/**
 * Lead-funnel events on public profiles — mirror the DB `leads` signals
 * (`trackLeadAction`) into PostHog for funnel analysis. Names match the DB types.
 */
export const LEAD_EVENTS = {
  profileView: "profile_view",
  packageClick: "package_click",
} as const;

/**
 * Affiliate link clicks (affiliate-links-spec.md §6.2). The server counts every
 * click in `affiliate_click_days` with no consent needed (no personal data);
 * this consent-gated event is only for funnel views. { id, surface }
 */
export const AFFILIATE_EVENTS = {
  click: "affiliate_click",
} as const;

/**
 * Chat assistant funnel (ai-chat-spec.md §13). Client-side, consent-gated like
 * everything else; NEVER carries message text, slot values or summaries —
 * states, counts and enum-ish flags only. The server-side metrics table
 * (`chat_turn_metrics`) is the store of record; these are for funnel views.
 */
export const CHAT_EVENTS = {
  opened: "chat_opened",
  turn: "chat_turn",                      // { state, lang, outcome, chip }
  branch: "chat_branch",                  // { branch }
  recommendShown: "chat_recommend_shown", // { count, relaxed }
  contactClick: "chat_contact_click",     // { slug }
  checklistShown: "chat_checklist_shown", // { checklist_id }
  checklistSaved: "chat_checklist_saved",
  // Created from the DASHBOARD menu, with no conversation behind it — a
  // distinct event, not a second `checklistSaved`, or the two entry points
  // become impossible to tell apart in the funnel. { checklist_id }
  checklistCreated: "checklist_created_from_dashboard",
  requestHandoff: "chat_request_handoff",
  // ⛔ The docked mini bar gets its OWN events, deliberately — see the
  // `checklistCreated` note above for the identical reasoning. The bar is a
  // SECOND entry point into /chat; firing `opened` from it would make the only
  // question worth asking — does it originate conversations, or merely relocate
  // clicks? — unanswerable. `chars` is a LENGTH, never the text: the reader's
  // message is not analytics data.
  miniChatExpanded: "mini_chat_expanded", // { path }
  miniChatSent: "mini_chat_sent",         // { path, chars, fromPrompt }
  // The docked bar's SEARCH variant (mini-search-fallback-spec.md) gets its own
  // pair, for the third time and the same reason: it occupies the same slot as
  // the assistant bar, so a shared event would make "does the fallback recover
  // the taps the assistant would have had, or is an empty slot just as good?"
  // unanswerable — which is the only question the fallback exists to answer.
  // { path, chars, target: "composer" | "search" } / { path, type }
  miniSearchSubmitted: "mini_search_submitted",
  miniSearchSuggestion: "mini_search_suggestion",
  // The HERO assistant is a THIRD entry point and gets its own pair, for the same
  // reason again: it replaces the site's primary search box, so "did promoting it
  // originate conversations, or just relocate them from the search funnel?" is the
  // only question worth asking, and a shared event makes it unanswerable. It needs
  // no `path` — it exists on the homepage alone. { chars } / { iconKey }
  heroAssistantSent: "hero_assistant_sent",
  heroPillAsked: "hero_pill_asked",
  report: "chat_report",
  loginGate: "chat_login_gate",           // anon tried to send
  newConversationGate: "chat_new_conversation_gate", // anon clicked New conversation
} as const;
